import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Stack, Typography, Box, TextField, FormControl, Button, Checkbox, Chip,
  FormControlLabel, CircularProgress, InputLabel, Select, MenuItem,
  InputAdornment, Tooltip, Dialog, DialogActions, DialogContent,
  DialogContentText, DialogTitle
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import InfoIcon from '@mui/icons-material/Info';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import SearchableMultiSelect from '../../../components/SearchableMultiSelect.js';
import { PRE_EXISTING_CONDITIONS } from '../../../constants/preExistingConditions.js';
import { DSM5_CONDITIONS } from '../../../constants/dsm5Conditions.js';
import { countryCodes } from '../../../constants/countryCodes.js';
import {
  getConnectionStatus,
  getMindvaultProfileByEmail,
  deriveNewClientConnectState,
  splitMindvaultName,
  normalizeMindvaultGender,
} from '../../../utils/scribeConnection.js';
import { CREDIT_CONNECTION_PRICE_CREDITS } from '../../../utils/creditWalletRpc.js';

const detectStateNeedsCredit = (status) => {
  const state = deriveNewClientConnectState(status);
  return state.view === 'not_connected' || state.view === 'booking_lapsed';
};

const NewPatientForm = ({
  userMode, setNewPatient, setFormSubmitted, formSubmitted, phoneNumber, setPhoneNumber,
  case_owner, setCaseOwner, setAlert, createPatient, firstName, setFirstName,
  lastName, setLastName, hkid, setHkid, gender, setGender, birth_date, setBirthDate,
  remarks, setRemarks, preExistingConditions, setPreExistingConditions,
  emergencyContact, setEmergencyContact, emergencyContactPerson, setEmergencyContactPerson,
  presentingConcerns, setPresentingConcerns, diagnosis, setDiagnosis, status, setStatus,
  countryCode, setCountryCode, teamMembersData, therapistUserId,
  email, setEmail
}) => {
  // MindVault detection + optional credit-connect, all local: none of it must
  // survive the form, and unmounting on setNewPatient(false) resets it for
  // free — clearFields() in MedicalScribe.js needs no changes.
  // detect.phase: 'idle' | 'checking' | 'done' | 'error'
  const [detect, setDetect] = useState({ phase: 'idle', result: null });
  const [prefillNote, setPrefillNote] = useState(null);
  const [connectChecked, setConnectChecked] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const latestEmailRef = useRef(email);
  const latestConnectCheckedRef = useRef(connectChecked);
  const latestProfileFieldsRef = useRef({ firstName, lastName, gender, phoneNumber });
  latestConnectCheckedRef.current = connectChecked;
  latestProfileFieldsRef.current = { firstName, lastName, gender, phoneNumber };

  const normalizedEmail = (email ?? '').trim();
  const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail);
  const accountFound = detect.phase === 'done' && !!detect.result?.hasMvAccount;
  const requiresCreditPurchase = detectStateNeedsCredit(detect.result);

  // Any email edit invalidates the detection, the checkbox, and the dialog:
  // stale results must never drive a purchase for a different address.
  useEffect(() => {
    latestEmailRef.current = email;
    setDetect({ phase: 'idle', result: null });
    setPrefillNote(null);
    setConnectChecked(false);
    setConfirmOpen(false);
  }, [email]);

  // Consent-gated prefill: the profile RPC returns a row only when the client
  // has shared with this therapist. Fill empty fields only — never overwrite
  // anything the therapist typed. Gender only flips from the untouched
  // 'Male' default to 'Female'; countryCode is skipped (its Select always
  // has a value).
  const syncProfileFromMindVault = useCallback(async (capturedEmail) => {
    const profile = await getMindvaultProfileByEmail(capturedEmail);
    if (!profile) return; // no consent / no account — a silent no-op, not an error
    if ((latestEmailRef.current ?? '').trim().toLowerCase() !== capturedEmail.toLowerCase()) return; // stale
    if (!latestConnectCheckedRef.current) return;
    const current = latestProfileFieldsRef.current;
    const nameChanged = !current.firstName.trim() || !current.lastName.trim();
    if (nameChanged) {
      const split = splitMindvaultName(profile.name);
      if (split) {
        if (!current.firstName.trim()) setFirstName(split.firstName);
        if (!current.lastName.trim() && split.lastName) setLastName(split.lastName);
      }
    }
    if (current.gender === 'Male' && normalizeMindvaultGender(profile.gender) === 'Female') {
      setGender('Female');
    }
    if (!current.phoneNumber.trim() && (profile.phone_number ?? '').trim()) {
      setPhoneNumber(profile.phone_number.trim());
    }
    setPrefillNote("Prefilled from the client's MindVault profile — check before saving.");
  }, [setFirstName, setLastName, setGender, setPhoneNumber]);

  // Checking is tied to the therapist's explicit opt-in. Results and any
  // prefill are discarded when the email or checkbox changes.
  useEffect(() => {
    if (!connectChecked || !validEmail || !therapistUserId) {
      setDetect({ phase: 'idle', result: null });
      setPrefillNote(null);
      return undefined;
    }

    let cancelled = false;
    const captured = normalizedEmail;
    setDetect({ phase: 'checking', result: null });
    setPrefillNote(null);

    const checkAccount = async () => {
      try {
        const status = await getConnectionStatus(therapistUserId, captured, { throwOnError: true });
        if (cancelled || (latestEmailRef.current ?? '').trim().toLowerCase() !== captured.toLowerCase()) return;
        setDetect({ phase: 'done', result: status });
        if (status.connected) await syncProfileFromMindVault(captured);
      } catch (err) {
        if (cancelled) return;
        console.error('MindVault connection check failed:', err);
        setDetect({ phase: 'error', result: null });
      }
    };

    checkAccount();
    return () => { cancelled = true; };
  }, [connectChecked, validEmail, normalizedEmail, therapistUserId, syncProfileFromMindVault]);

  const validateRequiredFields = () => {
    setFormSubmitted(true);
    if(case_owner.length === 0) {
      setAlert('Please fill all required fields', 'error');
      return false;
    }
    return true;
  };

  // The form-level Confirm remains the ordinary Scribe-only add path.
  const handleConfirm = () => {
    if (!validateRequiredFields()) return;
    if (connectChecked) {
      setAlert(
        accountFound
          ? 'Use “Add client by email” under the Email field to complete this connection.'
          : 'Uncheck the MindVault connection option to add this client without connecting.',
        'info'
      );
      return;
    }
    createPatient(firstName, lastName, phoneNumber, hkid, gender, birth_date, case_owner, remarks, preExistingConditions, emergencyContact, emergencyContactPerson, presentingConcerns, diagnosis, status, countryCode, email);
  };

  // The in-place email action adds the patient with their MindVault email.
  // If a credit purchase is required, the dialog is the explicit cost consent.
  const handleAddByEmail = () => {
    if (!validateRequiredFields() || !accountFound) return;
    if (requiresCreditPurchase) {
      setConfirmOpen(true);
      return;
    }
    createPatient(firstName, lastName, phoneNumber, hkid, gender, birth_date, case_owner, remarks, preExistingConditions, emergencyContact, emergencyContactPerson, presentingConcerns, diagnosis, status, countryCode, email);
  };

  const handleDialogConfirm = async () => {
    setSubmitting(true);
    const created = await createPatient(firstName, lastName, phoneNumber, hkid, gender, birth_date, case_owner, remarks, preExistingConditions, emergencyContact, emergencyContactPerson, presentingConcerns, diagnosis, status, countryCode, email, true);
    // created === true: createPatient already closed the form and this
    // component unmounts (dialog included). false: the insert failed and the
    // form stayed open — close the dialog so the error alert is visible.
    if (created === false) {
      setSubmitting(false);
      setConfirmOpen(false);
    }
  };

  const detectState = deriveNewClientConnectState(detect.result);

  return (
    <Grid size="grow">
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{marginBottom: '20px'}}>
        <Typography variant="h6"> New Client </Typography>
        <div style={{ display: 'flex'}}>
          <Box onClick={() => { setNewPatient(false); }} sx={{ width: '80px', textAlign: 'center', borderRadius: 30, backgroundColor: '#FFFFFF', padding: '5px 10px', color: "#29C0BB", cursor: 'pointer', mr: 2}}>
            Close
          </Box> 
          <Box
            onClick={handleConfirm}
            sx={{ width: '80px', textAlign: 'center', borderRadius: 30, backgroundColor: '#29C0BB', padding: '5px 10px', color: "#ffffff", cursor: 'pointer'}}>
            Confirm
          </Box>
        </div>
      </Stack>

      <Grid container spacing={2}>
        <Grid size={{xs: 12, md: 6}}>
          <TextField
            fullWidth
            label="First Name"
            variant="outlined"
            focused
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            placeholder="First Name" 
            sx={{ marginTop: '20px', marginBottom: '16px', backgroundColor: '#f5f5f5' }} 
          />
        </Grid>
        
        <Grid size={{xs: 12, md: 6}}>
          <TextField
            fullWidth
            label="Last Name"
            variant="outlined"
            focused
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            placeholder="Last Name" 
            sx={{ marginTop: '20px', marginBottom: '16px', backgroundColor: '#f5f5f5' }}
          />
        </Grid>

        <Grid size={{xs: 12, md: 6}}>
          <FormControl fullWidth>
            <InputLabel id="gender-label">Gender</InputLabel>
            <Select
              labelId="gender-label"
              id="gender"
              label="Gender"
              fullWidth
              value={gender}
              onChange={(e) => setGender(e.target.value)}
            >
              <MenuItem value="Male">Male</MenuItem>
              <MenuItem value="Female">Female</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid size={{xs: 12, md: 6}}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Date of Birth"
              value={birth_date}
              onChange={(newValue) => {
                const today = new Date();
                today.setHours(23, 59, 59, 999);
                
                if (newValue && newValue > today) {
                  setAlert('Date of birth cannot be in the future', 'error');
                  setBirthDate(null);
                  return;
                }
                setBirthDate(newValue);
              }}
              views={['year', 'month', 'day']}
              maxDate={(() => {
                const today = new Date();
                return today;
              })()}
              sx={{ width: '100%' }}
            />
          </LocalizationProvider>
        </Grid>

        <Grid size={{xs: 12, md: 6}}>
          <TextField
            fullWidth
            label="HKID Number"
            variant="outlined"
            focused
            value={hkid}
            onChange={(e) => setHkid(e.target.value)}
            placeholder="HKID Number"
            sx={{ backgroundColor: '#f5f5f5', marginBottom: '16px' }}
          />
        </Grid>

        <Grid size={{xs: 12, md: 6}}>
          <Grid container spacing={1}>
            <Grid size={5}>
              <FormControl fullWidth>
                <InputLabel id="country-select-label" focused>
                  Country/Region
                </InputLabel>
                <Select
                  labelId="country-select-label"
                  value={countryCode}
                  onChange={(e) => setCountryCode(e.target.value)}
                  label="Country/Region"
                  focused
                  sx={{ backgroundColor: '#f5f5f5' }}
                >
                  {countryCodes.map((country) => (
                    <MenuItem key={country.code} value={country.code}>
                      {country.name} (+{country.code})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={7}>
              <TextField
                fullWidth
                label="Phone Number"
                variant="outlined"
                focused
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                placeholder="Phone Number"
                sx={{ backgroundColor: '#f5f5f5' }}
              />
            </Grid>
          </Grid>
        </Grid>

        <Grid size={{xs: 12, md: 6}}>
          <TextField
            fullWidth
            label="Email"
            variant="outlined"
            focused
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email Address"
            sx={{ backgroundColor: '#f5f5f5', marginBottom: '16px' }}
            helperText="Optional — used to find the client's MindVault account. Sharing still requires client consent."
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip
                    title="Use the email registered with the MindVault app. The client must still consent before their data is shared."
                    arrow
                    placement="top"
                  >
                    <InfoIcon sx={{ color: '#29C0BB', cursor: 'pointer' }} />
                  </Tooltip>
                </InputAdornment>
              ),
            }}
          />

          {validEmail && therapistUserId && (
            <Box sx={{ mt: -1, mb: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={connectChecked}
                    onChange={(e) => setConnectChecked(e.target.checked)}
                    sx={{ '&.Mui-checked': { color: '#29C0BB' } }}
                  />
                }
                label="Connect with the client's MindVault account"
              />
            </Box>
          )}

          {detect.phase === 'checking' && (
            <Typography variant="body2" sx={{ color: '#6B7280' }}>Checking MindVault…</Typography>
          )}
          {detect.phase === 'error' && (
            <Typography variant="body2" sx={{ color: '#d32f2f' }}>
              Could not check this email. Please try again.
            </Typography>
          )}

          {connectChecked && detect.phase === 'done' && detectState.view === 'connected' && (
            <Box sx={{ mb: 1 }}>
              <Chip
                icon={<CheckCircleOutlineIcon />}
                label="Connected"
                sx={{ backgroundColor: '#DCFCE7', color: '#15803D', fontWeight: 600 }}
              />
              {prefillNote && (
                <Typography variant="body2" sx={{ color: '#6B7280', mt: 0.5 }}>
                  {prefillNote}
                </Typography>
              )}
            </Box>
          )}

          {connectChecked && detect.phase === 'done' && detectState.view === 'awaiting_consent' && (
            <Box sx={{ mb: 1 }}>
              <Chip
                label="Purchased, awaiting client consent in the MindVault app"
                sx={{ backgroundColor: '#FEF9C3', color: '#A16207', fontWeight: 600 }}
              />
              <Typography variant="body2" sx={{ color: '#6B7280', mt: 0.5 }}>
                No further purchase is needed.
              </Typography>
            </Box>
          )}

          {connectChecked && detect.phase === 'done' && detectState.view === 'not_connected' && (
            <Typography variant="body2" sx={{ color: '#6B7280', mb: 0.5 }}>
              MindVault account found — not connected yet.
            </Typography>
          )}

          {connectChecked && detect.phase === 'done' && detectState.view === 'no_account' && (
            <Box sx={{ mb: 0.5 }}>
              <Typography variant="body2" sx={{ color: '#6B7280' }}>
                No MindVault account for this email.
              </Typography>
              <Typography variant="caption" sx={{ color: '#6B7280' }}>
                Uncheck this option to add the client without connecting a MindVault account.
              </Typography>
            </Box>
          )}

          {connectChecked && detect.phase === 'done' && detectState.view === 'booking_lapsed' && (
            <Box sx={{ mb: 0.5 }}>
              <Typography variant="body2" sx={{ color: '#6B7280' }}>
                Booking connection lapsed — this client's last MindVault booking with you was more than 30 days ago.
              </Typography>
            </Box>
          )}

          {connectChecked && accountFound && detect.phase === 'done' && (
            <Button
              fullWidth
              variant="contained"
              onClick={handleAddByEmail}
              disabled={submitting}
              startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
              sx={{
                mt: 1, mb: 2, backgroundColor: '#29C0BB', borderRadius: '8px',
                textTransform: 'none', fontWeight: 600,
                '&:hover': { backgroundColor: '#1FA09B' },
              }}
            >
              Add client by email
            </Button>
          )}
          {connectChecked && detect.phase === 'checking' && (
            <Typography variant="body2" sx={{ color: '#6B7280', mb: 1 }}>
              Checking this email against MindVault…
            </Typography>
          )}
          {connectChecked && detect.phase === 'done' && accountFound && requiresCreditPurchase && (
            <Typography variant="caption" sx={{ display: 'block', color: '#6B7280', mb: 1 }}>
              This connection requires {CREDIT_CONNECTION_PRICE_CREDITS} credits. The next step will ask you to confirm before charging.
            </Typography>
          )}
        </Grid>

        <Grid size={{xs: 12, md: 6}}>
          <SearchableMultiSelect
            label="Case Owners"
            options={teamMembersData}
            value={case_owner}
            onChange={(e) => setCaseOwner(e.target.value)}
            error={formSubmitted && case_owner.length === 0}
            helperText={formSubmitted && case_owner.length === 0 ? 'Please select at least one case owner' : ''}
            required
            getOptionLabel={(option) => option.name || option}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            chipColor="#29C0BB"
            placeholder="Search or select case owners"
          />
        </Grid>

        <Grid size={{xs: 12, md: 6}}>
          <SearchableMultiSelect
            label="Pre-existing Conditions"
            options={PRE_EXISTING_CONDITIONS}
            value={preExistingConditions}
            onChange={(e) => setPreExistingConditions(e.target.value)}
            chipColor="#FF6B6B"
            placeholder="Search or select conditions"
          />
        </Grid>

        <Grid size={{xs: 12, md: 6}}>
          <SearchableMultiSelect
            label="Diagnosis (DSM-5)"
            options={DSM5_CONDITIONS}
            value={diagnosis}
            onChange={(e) => setDiagnosis(e.target.value)}
            chipColor="#9C27B0"
            placeholder="Search or select diagnosis"
          />
        </Grid>

        <Grid size={{xs: 12, md: 6}}>
          <TextField
            fullWidth
            label="Presenting Concerns"
            variant="outlined"
            focused
            value={presentingConcerns}
            onChange={(e) => setPresentingConcerns(e.target.value)}
            placeholder="Presenting Concerns"
            sx={{ backgroundColor: '#f5f5f5', marginBottom: '16px' }}
          />
        </Grid>

        <Grid size={{xs: 12, md: 6}}>
          <TextField
              fullWidth
              label="Remarks"
              variant="outlined"
              focused
              placeholder="Remarks"
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
          />
        </Grid>

        <Grid size={{xs: 12, md: 6}}>
          <FormControl fullWidth>
            <InputLabel id="status-label">Status</InputLabel>
            <Select
                labelId="status-label"
                id="status"
                label="Status"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
            >
                <MenuItem value="Active">Active</MenuItem>
                <MenuItem value="Inactive">Inactive</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid size={{xs: 12, md: 6}}>
          <TextField
            fullWidth
            label="Emergency Contact Person"
            variant="outlined"
            focused
            value={emergencyContactPerson}
            onChange={(e) => setEmergencyContactPerson(e.target.value)}
            placeholder="Emergency Contact Person"
            sx={{ backgroundColor: '#f5f5f5', marginBottom: '16px' }}
          />
        </Grid>

        <Grid size={{xs: 12, md: 6}}>
          <TextField
            fullWidth
            label="Emergency Contact"
            variant="outlined"
            focused
            value={emergencyContact}
            onChange={(e) => setEmergencyContact(e.target.value)}
            placeholder="Emergency Contact"
            sx={{ backgroundColor: '#f5f5f5', marginBottom: '16px' }}
          />
        </Grid>
      </Grid>

      {/* Credit-connect confirmation. The debit is immediate in every case —
          what varies is only when the connection goes live. Cancel touches
          nothing: no patient row, no RPC, no operation id. */}
      <Dialog
        open={confirmOpen}
        onClose={() => !submitting && setConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add client and connect MindVault?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Adding this client and connecting their MindVault account will deduct{' '}
            <strong>{CREDIT_CONNECTION_PRICE_CREDITS} credits</strong> from your credit wallet{' '}
            <strong>now</strong>. The connection is active from payment; the client must still
            consent to sharing in their MindVault app.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmOpen(false)}
            disabled={submitting}
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleDialogConfirm}
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={14} color="inherit" /> : undefined}
            sx={{
              backgroundColor: '#29C0BB', borderRadius: '8px', textTransform: 'none',
              fontWeight: 600, '&:hover': { backgroundColor: '#1FA09B' },
            }}
          >
            {submitting ? 'Adding…' : `Confirm and deduct ${CREDIT_CONNECTION_PRICE_CREDITS} credits`}
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export default NewPatientForm;
